import React from 'react';
import { StaticPage, TopbarContainer } from '..';
import { FormattedMessage } from '../../util/reactIntl';
import {
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
} from '../../components';
import { NamedLink } from '../../components';

import css from './PickupPage.module.css';
import driversLicenceImage from './images/driversLicence.png';
import locationImage from '../../components/SectionForHirersSteps/images/convenient-location.png';
import calendarImage from '../../components/SectionForHirersSteps/images/quick-booking.png';
import returnImage from './images/return.png';
import messagingImage from './images/messaging.png';
import IconBookmark from '../../components/IconBookmark/IconBookmark';

const PickupPage = () => {
  return (
    <StaticPage
      title="Trailer pick-up guide"
      schema={{
        '@context': 'http://schema.org',
        '@type': 'PickupPage',
        description: 'Pick-up guide for localtrailerhire.com.au',
        name: 'Pick-up page',
      }}
      className={css.root}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <ul className={css.sections}>
            <h1 className={css.pageTitle}>
              <FormattedMessage id="PickupPage.title" />
            </h1>
            <h2 className={css.subTitle}>Pick-up and Return Process</h2>
            <li className={css.section}>
              <div className={css.sectionContentNoTop}>
                <h2 className={css.navTitle}>In this guide</h2>
                <nav className={css.contentNav}>
                  <ul className={css.navList}>
                    <li>
                      <IconBookmark className={css.icon} size={'small'} />
                      <a href="#pickupGuide">Pick-up</a>
                    </li>
                    <li>
                      <IconBookmark size={'small'} className={css.icon} />
                      <a href="#returnGuide">Return</a>
                    </li>
                    <li>
                      <IconBookmark size={'small'} className={css.icon} />
                      <a href="#adjustmentsGuide">Adjustments</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </li>

            <div id="pickupGuide"></div>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <img
                  src={locationImage}
                  alt="my bookings location on page"
                  className={css.sectionImage}
                />
                <h2 className={css.sectionTitle}>Trailer pick-up guide</h2>
                <div className={css.sectionColumns}>
                  <div className={css.sectionColumn}>
                    <h3 className={css.sectionSubtitle}>Identification requirement</h3>
                    <ul>
                      <li>
                        <b>Bring your driver's licence:</b> This is essential for identity
                        verification.
                      </li>
                    </ul>
                  </div>
                  <div>
                    <img
                      src={driversLicenceImage}
                      alt="my bookings location on page"
                      className={css.sectionSmallImage}
                    />
                  </div>
                </div>
                <h3 className={css.sectionSubtitle}>Arrival instructions</h3>
                <ol>
                  <li>
                    <b>Let the Owner know you are at the trailer pick-up location:</b> Knock on the
                    door or ring the bell of the property to let the owner know you have arrived to
                    pick-up the trailer.
                  </li>
                  <li>
                    <b>Show your drivers licence:</b> The owner will verify your identity.
                  </li>
                  <li>
                    <b>Take photos:</b> In order to alleviate any challenges upon return, please
                    don’t forget to take photos of each side of the trailer at the beginning and end
                    of your hire. Remind the owner to do so as well!
                  </li>
                </ol>
              </div>
              <div className={css.sectionContent}>
                <h3 className={css.sectionSubtitle}>Safety tips</h3>
                <p>
                  We want you to have a smooth, safe and secure experience when hiring trailers
                  through Local Trailer Hire.
                </p>
                <ul>
                  <li>
                    <b>Stay safe: </b>Check out our practical tips in our{' '}
                    <NamedLink name="SafeHiringPage" className={css.link}>
                      Safe hiring guide
                    </NamedLink>{' '}
                    to help you select the right trailer, hitch it securely, and tow it safely.
                  </li>
                </ul>
              </div>
            </li>
            <div id="returnGuide"></div>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <img
                  src={returnImage}
                  alt="my bookings location on page"
                  className={css.sectionImage}
                />
                <h2 className={css.sectionTitle}>Trailer return guide</h2>
                <h3 className={css.sectionSubtitle}>Returning the trailer</h3>
                <ol>
                  <li>
                    <b>Clean the trailer:</b> Ensure it's returned in the same condition as it was
                    when you picked it up.
                  </li>
                  <li>
                    <b>Inspect for damage:</b> Check for any issues and notify the owner.
                  </li>
                  <li>
                    <b>Let the owner know you are at the trailer pick-up & return location:</b>{' '}
                    Knock on the door or ring the bell of the property to let the owner know you
                    have arrived to return the trailer.
                  </li>
                  <li>
                    <b>Take photos again:</b> In order to alleviate any challenges upon return,
                    please don’t forget to take photos of each side of the trailer at the beginning
                    and end of your hire. Remind the owner to do so as well!
                  </li>
                </ol>
              </div>
            </li>
            <div id="adjustmentsGuide"></div>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <img src={calendarImage} alt="clock icon" className={css.sectionImage} />
                <h2 className={css.sectionTitle}>Booking adjustments guide</h2>
                <h3 className={css.sectionSubtitle}>Change of plans? Running late?</h3>
                <ul>
                  <li>
                    <b>Notify the owner:</b> If you need to pick-up at a different time, contact the
                    owner through the booking page to confirm any schedule changes.
                  </li>
                  <li>
                    <b>Need to cancel?</b> Check out our{' '}
                    <NamedLink name="ExplainCancellationPage" className={css.link}>
                      Cancellations page
                    </NamedLink>
                    .
                  </li>
                </ul>
                <p>
                  Note: If you arrive to pick-up the trailer more than 30 minutes later than your
                  agreed pick-up time, you will not be eligible for a refund and the owner is not
                  required to provide you with the trailer for hire. See the{' '}
                  <NamedLink name="RentalTermsPage">Rental Contract</NamedLink> for the full rental
                  terms.
                </p>
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <img src={messagingImage} alt="conversation icon" className={css.sectionImage} />
                <h2 className={css.sectionTitle}>Communicating with the Owner</h2>
                <p>
                  If you have any questions, don't hesitate to reach out to the trailer owner —
                  they're here to help! You can easily send them a message through the booking page.
                </p>
                <p>
                  Just log in to localtrailerhire.com.au, go to "My bookings" (you can find it in
                  the burger menu in the top left if you're on a mobile), and select your booking.
                  At the bottom of the booking page, you'll see an option to message the owner
                  directly.
                </p>
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <h2 className={css.sectionTitle}>Useful rental information</h2>
                <ul>
                  <li>
                    <b>Pick-up on time:</b> Please arrive promptly for your pick-up. If you are more
                    than 30 minutes late, the trailer may no longer be available, and you will still
                    be charged the full rental fee without a refund if the owner cannot accommodate
                    the delay.
                  </li>
                  <li>
                    <b>Return on time:</b> Trailers returned late will incur an extra day's charge,
                    so be sure to return on time to avoid additional fees.
                  </li>
                  <li>
                    <b>Cancellations:</b> Cancelling within 24 hours of your pick-up time means no
                    refund is available. For more details, please refer to our{' '}
                    <NamedLink name="ExplainCancellationPage" className={css.link}>
                      Cancellations page
                    </NamedLink>
                    .
                  </li>
                  <li>
                    You can read the full rental terms in the{' '}
                    <NamedLink name="RentalTermsPage">Rental Contract</NamedLink>.
                  </li>
                </ul>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <h2 className={css.sectionTitle}>Couldn't find what you were looking for?</h2>
                <p>
                  Visit our <NamedLink name="HelpPage">Help Centre</NamedLink>
                </p>
              </div>
            </li>
          </ul>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

export default PickupPage;
